import { useEffect, useMemo, useState } from "react";
import { Trophy, User as UserIcon, CheckCircle2, Lock, Filter, ScrollText } from "lucide-react";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const INR = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

// Indian numbering with slash suffix (matches the reference image)
const rewardLabel = (n) =>
  `${Number(n).toLocaleString("en-IN")}/-`;

export default function Rewards() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [filterId, setFilterId] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterRank, setFilterRank] = useState("All");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/rewards-status");
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally { setLoading(false); }
    })();
    (async () => {
      try {
        const { data } = await api.get("/rewards-history");
        setHistory(data.items || []);
      } catch (e) {
        console.error("Failed to load rewards history:", e);
      } finally { setHistoryLoading(false); }
    })();
  }, []);

  const uniqueRanks = useMemo(() => {
    const s = new Set(history.map((h) => h.rank));
    return ["All", ...Array.from(s)];
  }, [history]);

  const filtered = useMemo(() => {
    const idQ = filterId.trim().toLowerCase();
    const nameQ = filterName.trim().toLowerCase();
    return history.filter((h) => {
      if (idQ && !h.user_code.toLowerCase().includes(idQ)) return false;
      if (nameQ && !(h.name || "").toLowerCase().includes(nameQ)) return false;
      if (filterRank !== "All" && h.rank !== filterRank) return false;
      return true;
    });
  }, [history, filterId, filterName, filterRank]);

  const formatAch = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const time = d.toLocaleTimeString("en-IN", {
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
      });
      return { date: `${dd}/${mm}/${yyyy}`, time };
    } catch { return { date: "—", time: "" }; }
  };

  const rankPill = (rank) => {
    const map = {
      Member: "bg-slate-100 text-slate-700",
      Executive: "bg-blue-100 text-blue-700",
      Promoter: "bg-emerald-100 text-emerald-700",
      Motivator: "bg-purple-100 text-purple-700",
      "Sr. Motivator": "bg-fuchsia-100 text-fuchsia-700",
      "Co-ordinator": "bg-amber-100 text-amber-800",
      "Sr. Co-ordinator": "bg-orange-100 text-orange-800",
      Manager: "bg-red-100 text-red-700",
      Director: "bg-rose-100 text-rose-800",
    };
    return `inline-block text-[11px] font-semibold px-2.5 py-1 rounded-full ${
      map[rank] || "bg-slate-100 text-slate-700"
    }`;
  };

  return (
    <div className="min-h-screen" style={{ background: "#560d0d" }}>
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 pb-16 space-y-6 text-white">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight flex items-center gap-2"
              style={{ fontFamily: "Outfit, sans-serif" }}>
            <Trophy size={28} className="text-amber-300" />
            Level Upgrade Rewards
          </h1>
          <p className="text-white/70 mt-1 text-sm">
            Bonus rewards you earn when your team hits each level milestone
          </p>
        </div>

        {loading || !data ? (
          <div className="py-20 flex justify-center">
            <div className="h-8 w-8 rounded-full border-4 border-white/40 border-t-white animate-spin" />
          </div>
        ) : (
          <>
            {/* Total earned card */}
            <div
              data-testid="total-rewards-card"
              className="rounded-2xl p-6 shadow-xl relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(135deg, #f59e0b 0%, #d97706 40%, #b45309 100%)",
              }}
            >
              <div className="absolute -right-6 -top-6 h-32 w-32 bg-white/15 rounded-full blur-2xl" />
              <div className="relative">
                <div className="text-[11px] uppercase tracking-widest text-white/85 font-bold">
                  Total Level Upgrade Rewards Earned
                </div>
                <div className="mt-2 text-4xl sm:text-5xl font-black text-white"
                     style={{ fontFamily: "Outfit, sans-serif" }}>
                  {INR(data.total_rewards)}
                </div>
                <div className="mt-2 text-xs text-white/80">
                  {data.slabs.filter((s) => s.achieved).length} of {data.slabs.length} slabs unlocked
                </div>
              </div>
            </div>

            {/* Slab table styled like reference */}
            <section
              className="rounded-2xl overflow-hidden shadow-2xl"
              style={{
                background:
                  "linear-gradient(180deg, #f59e0b 0%, #f59e0b 8%, #f8fafc 8%)",
              }}
            >
              <div
                className="grid grid-cols-2 items-center"
                style={{ background: "#1e3a8a" }}
              >
                <div className="px-5 py-3 text-white font-extrabold tracking-widest text-lg text-center border-r border-white/20"
                     style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "0.15em" }}>
                  USERS
                </div>
                <div className="px-5 py-3 text-amber-300 font-extrabold tracking-widest text-lg text-center italic"
                     style={{ fontFamily: "Outfit, sans-serif" }}>
                  Level Upgrade Rewards
                </div>
              </div>
              <ul data-testid="rewards-slabs" className="divide-y divide-slate-200">
                {data.slabs.map((s) => (
                  <li
                    key={s.depth}
                    data-testid={`reward-slab-${s.depth}`}
                    className={`grid grid-cols-2 items-center transition-colors ${
                      s.achieved ? "bg-emerald-50" : "bg-white"
                    }`}
                  >
                    <div className="px-5 py-3 flex items-center gap-3 border-r border-slate-200">
                      <UserIcon
                        size={18}
                        className={s.achieved ? "text-emerald-600" : "text-slate-600"}
                      />
                      <span className="font-semibold text-slate-900 text-sm sm:text-base">
                        {s.users_needed} - {s.rank}
                      </span>
                      {s.achieved ? (
                        <CheckCircle2 size={14} className="text-emerald-600 ml-1" />
                      ) : (
                        <Lock size={12} className="text-slate-400 ml-1" />
                      )}
                    </div>
                    <div className="px-5 py-3 text-right font-extrabold text-blue-900 tabular-nums"
                         style={{ fontFamily: "Outfit, sans-serif" }}>
                      {rewardLabel(s.reward)}
                    </div>
                    <div className="col-span-2 px-5 pb-3 -mt-1 flex items-center justify-between gap-3">
                      <div className="text-[11px] text-slate-600">
                        Level {s.depth} · Your team at this level:{" "}
                        <b className={s.achieved ? "text-emerald-700" : "text-slate-800"}>
                          {s.users_have}/{s.users_needed}
                        </b>
                      </div>
                      <div className="flex-1 max-w-[45%] h-1.5 rounded-full bg-slate-200 overflow-hidden">
                        <div
                          className={`h-full transition-all ${
                            s.achieved ? "bg-emerald-500" : "bg-blue-400"
                          }`}
                          style={{
                            width: `${Math.min(
                              100,
                              (s.users_have / s.users_needed) * 100
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            <div className="rounded-2xl bg-white/10 border border-white/15 p-4 text-xs text-white/80 leading-relaxed">
              <b className="text-amber-300">How it works:</b> Rewards are unlocked automatically the
              moment your qualified team at each level hits the target size. Reward totals are
              cumulative and added to your Total Earnings on the dashboard.
            </div>

            {/* Reward History — Team Members */}
            <section data-testid="reward-history-section" className="bg-white rounded-2xl shadow-lg overflow-hidden text-slate-900">
              <div className="px-5 pt-5 pb-4 border-b border-slate-100">
                <h2 className="text-xl font-extrabold flex items-center gap-2"
                    style={{ fontFamily: "Outfit, sans-serif" }}>
                  <ScrollText size={20} className="text-red-700" />
                  Reward History - Team Members
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  View all team members who contributed to your level upgrades
                </p>
              </div>

              <div className="p-5 border-b border-slate-100">
                <div className="flex items-center gap-2 text-xs text-slate-500 font-semibold uppercase tracking-widest mb-3">
                  <Filter size={13} /> Filters
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-600">User ID</label>
                    <input
                      data-testid="history-filter-id"
                      value={filterId}
                      onChange={(e) => setFilterId(e.target.value)}
                      placeholder="Search by User ID..."
                      className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">User Name</label>
                    <input
                      data-testid="history-filter-name"
                      value={filterName}
                      onChange={(e) => setFilterName(e.target.value)}
                      placeholder="Search by User Name..."
                      className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Rank</label>
                    <select
                      data-testid="history-filter-rank"
                      value={filterRank}
                      onChange={(e) => setFilterRank(e.target.value)}
                      className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none"
                    >
                      {uniqueRanks.map((r) => (
                        <option key={r} value={r}>{r === "All" ? "All Ranks" : r}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest font-bold">
                    <tr>
                      <th className="text-left px-4 py-3">SR. NO.</th>
                      <th className="text-left px-4 py-3">User ID</th>
                      <th className="text-left px-4 py-3">User Name</th>
                      <th className="text-left px-4 py-3">Depth</th>
                      <th className="text-left px-4 py-3">Rank</th>
                      <th className="text-left px-4 py-3">Achievement Date</th>
                    </tr>
                  </thead>
                  <tbody data-testid="reward-history-table" className="divide-y divide-slate-100">
                    {historyLoading ? (
                      <tr><td colSpan={6} className="text-center py-10 text-slate-500">Loading…</td></tr>
                    ) : filtered.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-10 text-slate-500">
                        No matching team members yet.
                      </td></tr>
                    ) : filtered.map((h, i) => {
                      const a = formatAch(h.achievement_date);
                      return (
                        <tr key={h.user_code} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-semibold text-slate-800">{i + 1}</td>
                          <td className="px-4 py-3 font-mono text-blue-700 font-semibold">{h.user_code}</td>
                          <td className="px-4 py-3 font-semibold">{h.name}</td>
                          <td className="px-4 py-3">{h.depth}</td>
                          <td className="px-4 py-3">
                            <span className={rankPill(h.rank)}>{h.rank}</span>
                          </td>
                          <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                            <span className="font-semibold">{a.date}</span>{" "}
                            <span className="text-slate-500 text-xs">{a.time}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 text-xs text-slate-600">
                Showing <b>{filtered.length}</b> of <b>{history.length}</b> team members
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
